/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/

/** @jsx jsx */
import { css, jsx } from "@emotion/core"
import { FC, useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Button, Col, Row, Input, List, Radio, Drawer } from "@duxton-web/ui"
import {
  ChevronRightUniversal,
  ChevronLeftUniversal,
  SearchUniversal,
} from "@duxton-web/icons"

import { RadioChangeEvent } from "@duxton-web/ui/lib/radio"
import { useTranslation, useLocaleContext } from "common/utils/i18n"
import { OverflowTip } from "components/common"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { GrabCategory, ItemClass } from "types/models/menu"

import { debounce } from "lodash"
import { storageHelper } from "utils/storage"
import { LoadingComponent } from "../LoadingComponent"
import {
  drawerFooter,
  listSuggestedCategory,
  listAllGrabCategory,
  searchWrapper,
  titleContent,
  drawer,
} from "../../styles"

interface DepartmentTaggingDrawerProps {
  visible: boolean
  onClose(): void
}

export const DepartmentTaggingDrawer: FC<DepartmentTaggingDrawerProps> = ({
  visible,
  onClose,
}: DepartmentTaggingDrawerProps) => {
  const {
    suggestedDepartments,
    isFetchingDataForGrabCategory,
    departmentsForGrabCategory,
    subDepartmentInDepartmentListForGrabCategory,
    merchantGroupID,
    menuGroupDetail: { menuGroupID },
    drawer: { item },
    merchantID,
    selectedGrabCategory,
    hasMoreGrabCategory,
    offsetGrabCategory,
    predictionIDGrabCategory,
    isMenuGroup,
  } = useSelector((state: AppState) => state.menu)

  const countryID = storageHelper.getUserCountryID()
  const locale = useLocaleContext("locale")
  const dispatch = useDispatch()
  const [selectedCategory, setSelectedCategory] = useState<GrabCategory>()
  const [searchValue, setSearchValue] = useState("")
  const loadMoreBtn = useTranslation("common.loadMore")
  const shouldPostFeedbackGrabCategory =
    !!item?.itemID &&
    suggestedDepartments?.length &&
    !!selectedGrabCategory?.itemClassID &&
    !!selectedGrabCategory?.parentClassID

  useEffect(() => {
    dispatch(
      actionCreator.menu.fetchDepartmentsForGrabCategory({
        locale,
        menuGroupID,
        merchantGroupID,
        merchantID,
      }),
    )
  }, [dispatch, menuGroupID, merchantGroupID, locale, merchantID])

  useEffect(() => {
    // Send prediction feedback for Grab Category
    if (shouldPostFeedbackGrabCategory) {
      dispatch(
        actionCreator.menu.postFeedbackGrabCategory(
          {
            predictionID: predictionIDGrabCategory as string,
            suggestedDepartments,
            selectedDepartmentID: selectedGrabCategory?.parentClassID as string,
            selectedSubDepartmentID: selectedGrabCategory?.itemClassID as string,
            itemID: item?.itemID as string,
          },
          merchantID,
          merchantGroupID,
          menuGroupID,
        ),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [suggestedDepartments])

  const handleSearchDepartments = debounce((val: string) => {
    if (val.length > 2 || val.length === 0) {
      setSearchValue(val)

      // Set selectedCategory to default value
      setSelectedCategory(undefined)

      dispatch(
        actionCreator.menu.fetchDepartmentsForGrabCategory({
          locale,
          searchName: val,
          menuGroupID,
          merchantGroupID,
          merchantID,
        }),
      )
    }
  }, 500)

  const handleOnClickDepartment = (dept: ItemClass) => {
    // Set selectedCategory to default value
    setSelectedCategory(undefined)

    dispatch(
      actionCreator.menu.toggleDrawer({
        showSubDepartmentTaggingDrawer: true,
      }),
    )

    dispatch(
      actionCreator.menu.fetchSubdepartmentsForGrabCategory({
        locale,
        parentID: dept?.ItemClassID,
        parentName: dept?.Name,
        menuGroupID,
        merchantGroupID,
        merchantID,
      }),
    )
  }

  const handleOnChangeSuggestedDepartment = (val: string) => {
    const selectedSuggestedDepartment = suggestedDepartments?.find(
      suggestedDept => suggestedDept.subDepartmentID === val,
    )

    setSelectedCategory({
      itemClassID: selectedSuggestedDepartment?.subDepartmentID as string,
      itemClassName: selectedSuggestedDepartment?.subDepartmentName as string,
      parentClassID: selectedSuggestedDepartment?.departmentID as string,
      parentClassName: selectedSuggestedDepartment?.departmentName,
    })
  }

  const handleOnChangeSubDepartment = (val: string) => {
    const selectedSubDepartment = subDepartmentInDepartmentListForGrabCategory?.find(
      subDept => subDept?.ItemClassID === val,
    )

    setSelectedCategory({
      itemClassID: selectedSubDepartment?.ItemClassID as string,
      itemClassName: selectedSubDepartment?.Name as string,
      parentClassID: selectedSubDepartment?.ParentID as string,
      parentClassName: selectedSubDepartment?.ParentClassName,
    })
  }

  const handleCancelDepartmentDrawer = () => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        showDepartmentTaggingDrawer: false,
      }),
    )
  }

  const handleSaveDepartmentDrawer = () => {
    dispatch(
      actionCreator.menu.toggleDrawer({
        showDepartmentTaggingDrawer: false,
      }),
    )

    dispatch(
      actionCreator.menu.getSelectedGrabCategory(
        selectedCategory as GrabCategory,
      ),
    )
    if (selectedCategory?.itemClassID) {
      dispatch(
        actionCreator.menu.fetchSupportedAttributeCluster(
          merchantID,
          merchantGroupID,
          isMenuGroup,
          menuGroupID,
          {
            itemClassIDs: selectedCategory?.itemClassID,
            countryID,
            businessType: "shop",
          },
        ),
      )
    }
  }

  const handleLoadMore = () => {
    dispatch(
      actionCreator.menu.loadMoreDepartmentsForGrabCategory({
        locale,
        menuGroupID,
        merchantGroupID,
        merchantID,
        offsetGrabCategory,
        isLoadingMore: true,
      }),
    )
  }

  const loadMore =
    !isFetchingDataForGrabCategory && hasMoreGrabCategory ? (
      <div
        css={css`
          padding: 24px;
          text-align: center;
        `}
      >
        <Button onClick={handleLoadMore} data-testid="load-more-btn">
          {loadMoreBtn}
        </Button>
      </div>
    ) : (
      ""
    )

  return (
    <Drawer
      css={drawer}
      title="Add Grab Category"
      width={450}
      open={visible}
      onClose={onClose}
      destroyOnClose
      closeIcon={<ChevronLeftUniversal />}
    >
      <Row css={searchWrapper}>
        <Col span={22}>
          <Input
            data-testid="search-category"
            placeholder="Search by Category name"
            prefix={<SearchUniversal />}
            onChange={e => handleSearchDepartments(e.target.value)}
          />
        </Col>
      </Row>
      {isFetchingDataForGrabCategory ? (
        <LoadingComponent />
      ) : (
        <div>
          {!!suggestedDepartments?.length && !searchValue && (
            <div>
              <div data-testid="suggested-categories-title" css={titleContent}>
                SUGGESTED CATEGORIES
              </div>
              <div css={listSuggestedCategory}>
                <Radio.Group
                  onChange={(e: RadioChangeEvent) => {
                    handleOnChangeSuggestedDepartment(e.target.value)
                  }}
                  data-testid="suggested-categories-list"
                  defaultValue={selectedGrabCategory?.itemClassID}
                >
                  {suggestedDepartments?.map(suggestedDepartment => (
                    <Radio
                      value={suggestedDepartment.subDepartmentID}
                      key={suggestedDepartment.subDepartmentID}
                    >
                      <OverflowTip className="subdepartment">
                        {suggestedDepartment.subDepartmentName}
                      </OverflowTip>
                      <OverflowTip className="department">
                        in {suggestedDepartment.departmentName}
                      </OverflowTip>
                    </Radio>
                  ))}
                </Radio.Group>
              </div>
            </div>
          )}
          <div>
            <div data-testid="all-grab-categories-title" css={titleContent}>
              ALL GRAB CATEGORIES
            </div>
            <div css={listAllGrabCategory}>
              <Radio.Group
                onChange={(e: any) => {
                  handleOnChangeSubDepartment(e.target.value)
                }}
                data-testid="all-grab-subcategories-list"
              >
                {subDepartmentInDepartmentListForGrabCategory?.map(subDept => (
                  <Radio
                    value={subDept?.ItemClassID}
                    key={subDept?.ItemClassID}
                  >
                    <OverflowTip className="subdepartment">
                      {subDept?.Name}
                    </OverflowTip>
                    <OverflowTip className="department">
                      in {subDept?.ParentClassName}
                    </OverflowTip>
                  </Radio>
                ))}
              </Radio.Group>
              <List
                data-testid="all-grab-categories-list"
                itemLayout="horizontal"
                dataSource={departmentsForGrabCategory}
                renderItem={department => (
                  <List.Item
                    className="item"
                    onClick={() => handleOnClickDepartment(department)}
                  >
                    <div className="department-name-wrapper">
                      <OverflowTip className="department-name">
                        {department?.Name}
                      </OverflowTip>
                      <Col>
                        <ChevronRightUniversal />
                      </Col>
                    </div>
                  </List.Item>
                )}
                locale={
                  subDepartmentInDepartmentListForGrabCategory?.length
                    ? { emptyText: "" }
                    : undefined
                } // Hide/show empty status in list
                loadMore={loadMore}
              />
            </div>
          </div>
        </div>
      )}
      <div css={drawerFooter}>
        <Row gutter={12}>
          <Col span={6}>
            <Button
              data-testid="cancel-button"
              type="default"
              block
              onClick={handleCancelDepartmentDrawer}
            >
              Cancel
            </Button>
          </Col>
          <Col flex="auto">
            <Button
              data-testid="confirm-button"
              type="primary"
              block
              disabled={!selectedCategory}
              onClick={handleSaveDepartmentDrawer}
            >
              Confirm
            </Button>
          </Col>
        </Row>
      </div>
    </Drawer>
  )
}
